import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

// ============================================================
// Construtor de Relatórios Dinâmicos
// Consome GET /relatorios/dicionario para o catálogo de campos
// e POST /relatorios/executar para gerar o SQL e retornar dados.
// ============================================================

const labelStyle = {
  display: 'block',
  fontSize: '0.78rem',
  fontWeight: 600,
  color: 'var(--gray-600)',
  marginBottom: '0.3rem',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
};

const inputStyle = {
  padding: '0.45rem 0.65rem',
  border: '1px solid var(--gray-300)',
  borderRadius: '6px',
  fontSize: '0.875rem',
  color: 'var(--gray-700)',
  background: 'white',
  minWidth: '140px',
};

const cardStyle = {
  background: 'white',
  borderRadius: '8px',
  border: '1px solid var(--gray-200)',
};

// ============================================================
// Gráfico de barras horizontais em SVG puro (sem dependências)
// Renderizado somente quando há 1 dimensão + 1 métrica e ≤ 30 linhas
// ============================================================
function BarChart({ linhas, dimensaoId, metricaId, dimLabel, metLabel }) {
  if (!linhas || linhas.length === 0 || linhas.length > 30) return null;

  const values = linhas.map((row) => ({
    label: String(row[dimensaoId] ?? '(vazio)'),
    value: parseFloat(row[metricaId]) || 0,
  }));

  const maxVal = Math.max(...values.map((v) => v.value), 1);
  const barH = 26;
  const gap = 5;
  const labelW = 160;
  const barMaxW = 320;
  const valW = 60;
  const svgW = labelW + barMaxW + valW;
  const svgH = values.length * (barH + gap) + 30;

  return (
    <div style={{ padding: '1rem 1rem 0.5rem', borderBottom: '1px solid var(--gray-100)' }}>
      <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--gray-500)', marginBottom: '0.75rem' }}>
        {metLabel} por {dimLabel}
      </div>
      <svg
        width="100%"
        viewBox={`0 0 ${svgW} ${svgH}`}
        style={{ fontFamily: 'inherit', display: 'block', maxWidth: '680px' }}
      >
        {values.map((v, i) => {
          const barW = Math.max((v.value / maxVal) * barMaxW, v.value > 0 ? 3 : 0);
          const y = i * (barH + gap) + 20;
          const displayLabel = v.label.length > 22 ? v.label.slice(0, 21) + '…' : v.label;
          return (
            <g key={i}>
              <text
                x={labelW - 8}
                y={y + barH / 2 + 4}
                textAnchor="end"
                fontSize="11"
                fill="#555"
              >
                {displayLabel}
              </text>
              <rect
                x={labelW}
                y={y}
                width={barW}
                height={barH}
                fill="#2563eb"
                rx="3"
                opacity="0.82"
              />
              <text
                x={labelW + barW + 6}
                y={y + barH / 2 + 4}
                fontSize="11"
                fill="#333"
              >
                {v.value % 1 === 0 ? v.value : v.value.toFixed(1)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ============================================================
// Sugestões de perguntas para o Modo IA
// ============================================================
const SUGESTOES = [
  'Quantidade de atendimentos por setor neste mês, com contagem',
  'NPS médio (nota) por setor nos últimos 30 dias',
  'Quantidade de atendimentos com status ABERTO agrupado por atendente (nome do usuário)',
  'Top 10 beneficiários com mais atendimentos em 2026, mostrando nome e quantidade',
  'Top 20 maiores devedores com nome, CPF, valor total da dívida, dias de atraso e quantidade de boletos',
  'Quantidade de atendimentos por canal_entrada (TELEFONICO, EMAIL, WHATSAPP, CHAT, APP) agrupado por mês',
  'Top 10 categorias de atendimento mais frequentes com contagem',
  'Quantidade de atendimentos FINALIZADOS e total por setor, com taxa percentual',
  'Quantidade de atendimentos por dia da semana (segunda a domingo)',
  'Beneficiários inadimplentes com mais de 90 dias de atraso, mostrando nome, CPF, valor e dias',
];

// ============================================================
// Modo IA — componente separado para organização
// ============================================================
function ModoIA({ isAdmin }) {
  const [pergunta, setPergunta] = useState('');
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [sqlGerado, setSqlGerado] = useState(null);
  const [showSql, setShowSql] = useState(false);
  const [modoVisualizacao, setModoVisualizacao] = useState('tabela'); // 'tabela' | 'grafico'
  const textareaRef = useRef(null);

  const consultar = async (perguntaOverride) => {
    const q = (perguntaOverride || pergunta).trim();
    if (!q) return;
    setLoading(true);
    setErro(null);
    setResultado(null);
    setSqlGerado(null);
    try {
      const r = await api.post('/relatorios/ia/consultar', { pergunta: q });
      setResultado(r.data);
      setSqlGerado(r.data.sql_gerado || null);
      // Auto-seleciona gráfico quando há 1 col texto + 1 col numérica e ≤30 linhas
      const cols = r.data.colunas || [];
      const temTexto = cols.some((c) => c.tipo === 'texto');
      const temNumero = cols.some((c) => c.tipo === 'numero');
      if (temTexto && temNumero && r.data.total <= 30 && r.data.total > 0) {
        setModoVisualizacao('grafico');
      } else {
        setModoVisualizacao('tabela');
      }
    } catch (e) {
      setErro(e.response?.data?.detail || 'Erro ao consultar');
    } finally {
      setLoading(false);
    }
  };

  const exportarCSV = () => {
    if (!resultado) return;
    const { colunas, linhas } = resultado;
    const escape = (v) => {
      const s = v === null || v === undefined ? '' : String(v);
      return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [
      colunas.map((c) => c.label).join(','),
      ...linhas.map((row) => colunas.map((c) => escape(row[c.id])).join(',')),
    ].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `consulta_ia_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Detecta colunas para o gráfico — usa primeira texto como dimensão e ÚLTIMA numero como métrica
  const colTexto = resultado?.colunas?.find((c) => c.tipo === 'texto');
  const colsNumero = resultado?.colunas?.filter((c) => c.tipo === 'numero') || [];
  const colNumero = colsNumero.length > 0 ? colsNumero[colsNumero.length - 1] : null;
  const podeGrafico = colTexto && colNumero && resultado?.total > 0 && resultado?.total <= 30;

  return (
    <div>
      {/* Caixa de pergunta */}
      <div style={{ ...cardStyle, padding: '1.25rem', marginBottom: '1rem' }}>
        <label style={{ ...labelStyle, marginBottom: '0.6rem', fontSize: '0.85rem' }}>
          Descreva o que você quer ver
        </label>
        <div style={{ display: 'flex', gap: '0.65rem', alignItems: 'flex-start' }}>
          <textarea
            ref={textareaRef}
            value={pergunta}
            onChange={(e) => setPergunta(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                consultar();
              }
            }}
            placeholder="Ex: Total de atendimentos por setor este mês..."
            rows={2}
            style={{
              flex: 1,
              padding: '0.6rem 0.75rem',
              border: '1px solid var(--gray-300)',
              borderRadius: '6px',
              fontSize: '0.9rem',
              color: 'var(--gray-700)',
              resize: 'vertical',
              fontFamily: 'inherit',
              lineHeight: 1.5,
            }}
          />
          <button
            onClick={() => consultar()}
            disabled={loading || !pergunta.trim()}
            style={{
              padding: '0.6rem 1.25rem',
              background: !pergunta.trim() ? 'var(--gray-300)' : '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: loading || !pergunta.trim() ? 'not-allowed' : 'pointer',
              fontWeight: 700,
              fontSize: '0.875rem',
              whiteSpace: 'nowrap',
              opacity: loading ? 0.75 : 1,
              alignSelf: 'stretch',
            }}
          >
            {loading ? '⏳ Consultando...' : '✦ Consultar'}
          </button>
        </div>

        {/* Sugestões */}
        <div style={{ marginTop: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
          {SUGESTOES.map((s) => (
            <button
              key={s}
              onClick={() => { setPergunta(s); setTimeout(() => consultar(s), 50); }}
              style={{
                padding: '0.3rem 0.7rem',
                background: 'var(--gray-100)',
                border: '1px solid var(--gray-200)',
                borderRadius: '20px',
                fontSize: '0.78rem',
                color: 'var(--gray-600)',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#2563eb'; e.currentTarget.style.borderColor = '#bfdbfe'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--gray-100)'; e.currentTarget.style.color = 'var(--gray-600)'; e.currentTarget.style.borderColor = 'var(--gray-200)'; }}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Erro */}
      {erro && (
        <div style={{ padding: '0.9rem 1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#991b1b', marginBottom: '1rem', fontSize: '0.875rem' }}>
          <strong>Erro:</strong> {erro}
        </div>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div style={{ ...cardStyle, padding: '2rem', textAlign: 'center', color: 'var(--gray-400)' }}>
          <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>✦</div>
          A IA está gerando o SQL e consultando o banco...
        </div>
      )}

      {/* Resultados */}
      {resultado && !loading && (
        <div style={cardStyle}>
          {/* Barra de ações do resultado */}
          <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--gray-100)', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.82rem', color: 'var(--gray-500)', marginRight: '0.25rem' }}>
              {resultado.total.toLocaleString('pt-BR')} resultado{resultado.total !== 1 ? 's' : ''}
            </span>

            {podeGrafico && (
              <>
                <button
                  onClick={() => setModoVisualizacao('tabela')}
                  style={{
                    padding: '0.3rem 0.75rem',
                    border: '1px solid var(--gray-200)',
                    borderRadius: '5px',
                    background: modoVisualizacao === 'tabela' ? '#2563eb' : 'var(--gray-100)',
                    color: modoVisualizacao === 'tabela' ? 'white' : 'var(--gray-600)',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    fontWeight: 500,
                  }}
                >
                  ☰ Tabela
                </button>
                <button
                  onClick={() => setModoVisualizacao('grafico')}
                  style={{
                    padding: '0.3rem 0.75rem',
                    border: '1px solid var(--gray-200)',
                    borderRadius: '5px',
                    background: modoVisualizacao === 'grafico' ? '#2563eb' : 'var(--gray-100)',
                    color: modoVisualizacao === 'grafico' ? 'white' : 'var(--gray-600)',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    fontWeight: 500,
                  }}
                >
                  ▦ Gráfico
                </button>
              </>
            )}

            <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={exportarCSV}
                style={{ padding: '0.3rem 0.75rem', border: '1px solid var(--gray-200)', borderRadius: '5px', background: 'var(--gray-100)', color: 'var(--gray-600)', cursor: 'pointer', fontSize: '0.8rem' }}
              >
                ↓ CSV
              </button>
              {isAdmin && sqlGerado && (
                <button
                  onClick={() => setShowSql((v) => !v)}
                  style={{ padding: '0.3rem 0.75rem', border: '1px solid var(--gray-200)', borderRadius: '5px', background: 'var(--gray-100)', color: 'var(--gray-600)', cursor: 'pointer', fontSize: '0.8rem' }}
                >
                  {showSql ? 'Ocultar SQL' : 'Ver SQL'}
                </button>
              )}
            </div>
          </div>

          {/* SQL (admin) */}
          {showSql && sqlGerado && (
            <div style={{ padding: '0.75rem 1rem', background: '#1e293b', borderBottom: '1px solid var(--gray-100)' }}>
              <pre style={{ margin: 0, fontSize: '0.78rem', color: '#94a3b8', overflowX: 'auto', whiteSpace: 'pre-wrap' }}>
                {sqlGerado}
              </pre>
            </div>
          )}

          {/* Gráfico */}
          {modoVisualizacao === 'grafico' && podeGrafico && (
            <BarChart
              linhas={resultado.linhas}
              dimensaoId={colTexto.id}
              metricaId={colNumero.id}
              dimLabel={colTexto.label}
              metLabel={colNumero.label}
            />
          )}

          {/* Tabela */}
          {modoVisualizacao === 'tabela' && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ background: 'var(--gray-50)' }}>
                    {resultado.colunas.map((col) => (
                      <th key={col.id} style={{ padding: '0.7rem 1rem', textAlign: col.tipo === 'numero' ? 'right' : 'left', fontWeight: 600, color: 'var(--gray-600)', borderBottom: '2px solid var(--gray-200)', whiteSpace: 'nowrap', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {resultado.linhas.length === 0 ? (
                    <tr><td colSpan={resultado.colunas.length} style={{ padding: '2rem', textAlign: 'center', color: 'var(--gray-400)' }}>Nenhum resultado</td></tr>
                  ) : (
                    resultado.linhas.map((row, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? 'white' : 'var(--gray-50)' }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = '#eff6ff')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = i % 2 === 0 ? 'white' : 'var(--gray-50)')}
                      >
                        {resultado.colunas.map((col) => (
                          <td key={col.id} style={{ padding: '0.55rem 1rem', color: 'var(--gray-700)', textAlign: col.tipo === 'numero' ? 'right' : 'left', fontVariantNumeric: col.tipo === 'numero' ? 'tabular-nums' : 'normal' }}>
                            {row[col.id] === null || row[col.id] === undefined
                              ? <span style={{ color: 'var(--gray-300)' }}>—</span>
                              : col.tipo === 'numero'
                                ? Number(row[col.id]).toLocaleString('pt-BR', { maximumFractionDigits: 2 })
                                : String(row[col.id])}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Modo MV — relatórios curados que leem do Oracle do plano de saúde
// Consome GET /relatorios/mv/catalogo e POST /relatorios/mv/executar.
// ============================================================
function ModoMV({ isAdmin }) {
  const [catalogo, setCatalogo] = useState(null);
  const [relatorioId, setRelatorioId] = useState('');
  const [params, setParams] = useState({});
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [sqlGerado, setSqlGerado] = useState(null);
  const [showSql, setShowSql] = useState(false);

  // Carrega o catálogo de relatórios MV
  useEffect(() => {
    api.get('/relatorios/mv/catalogo')
      .then((r) => {
        setCatalogo(r.data);
        const primeiro = r.data.relatorios?.[0];
        if (primeiro) {
          setRelatorioId(primeiro.id);
          const defaults = {};
          (primeiro.params || []).forEach((p) => { defaults[p.id] = p.default; });
          setParams(defaults);
        }
      })
      .catch((e) => setErro(e.response?.data?.detail || 'Erro ao carregar catálogo MV'));
  }, []);

  const relatorioAtual = catalogo?.relatorios?.find((r) => r.id === relatorioId);

  const trocarRelatorio = (id) => {
    setRelatorioId(id);
    const rel = catalogo?.relatorios?.find((r) => r.id === id);
    const defaults = {};
    (rel?.params || []).forEach((p) => { defaults[p.id] = p.default; });
    setParams(defaults);
    setResultado(null);
  };

  const executar = async () => {
    if (!relatorioId) return;
    setLoading(true);
    setErro(null);
    try {
      const r = await api.post('/relatorios/mv/executar', { relatorio_id: relatorioId, params });
      setResultado(r.data);
      setSqlGerado(r.data.sql_gerado || null);
    } catch (e) {
      setErro(e.response?.data?.detail || 'Erro ao executar relatório');
      setResultado(null);
    } finally {
      setLoading(false);
    }
  };

  const exportarCSV = () => {
    if (!resultado) return;
    const { colunas, linhas } = resultado;
    const escape = (v) => {
      const s = v === null || v === undefined ? '' : String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [
      colunas.map((c) => c.label).join(','),
      ...linhas.map((row) => colunas.map((c) => escape(row[c.id])).join(',')),
    ].join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mv_${relatorioId}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      {/* Aviso se a conexão MV não estiver configurada neste ambiente */}
      {catalogo && !catalogo.disponivel && (
        <div style={{ padding: '0.9rem 1rem', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', color: '#92400e', marginBottom: '1rem', fontSize: '0.875rem' }}>
          <strong>Conexão com o MV não configurada neste ambiente.</strong> Defina as
          variáveis <code>ORACLE_MV_*</code> no backend para habilitar estes relatórios.
        </div>
      )}

      {/* Seleção do relatório + parâmetros */}
      <div style={{ ...cardStyle, padding: '1.25rem', marginBottom: '1rem', display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label style={labelStyle}>Relatório</label>
          <select value={relatorioId} onChange={(e) => trocarRelatorio(e.target.value)} style={{ ...inputStyle, minWidth: '260px' }}>
            {(catalogo?.relatorios || []).map((r) => (
              <option key={r.id} value={r.id}>{r.label}</option>
            ))}
          </select>
        </div>

        {(relatorioAtual?.params || []).map((p) => (
          <div key={p.id}>
            <label style={labelStyle}>{p.label}</label>
            <select
              value={params[p.id] ?? p.default}
              onChange={(e) => setParams((prev) => ({ ...prev, [p.id]: e.target.value }))}
              style={inputStyle}
            >
              {p.opcoes.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          </div>
        ))}

        <button
          onClick={executar}
          disabled={loading || !relatorioId}
          style={{
            padding: '0.55rem 1.2rem',
            background: !relatorioId ? 'var(--gray-300)' : '#2563eb',
            color: 'white', border: 'none', borderRadius: '6px',
            cursor: loading || !relatorioId ? 'not-allowed' : 'pointer',
            fontWeight: 700, fontSize: '0.875rem', opacity: loading ? 0.75 : 1,
          }}
        >
          {loading ? 'Consultando...' : 'Executar'}
        </button>
      </div>

      {/* Descrição do relatório */}
      {relatorioAtual?.descricao && (
        <div style={{ fontSize: '0.82rem', color: 'var(--gray-500)', marginBottom: '1rem' }}>
          {relatorioAtual.descricao}
        </div>
      )}

      {/* Erro */}
      {erro && (
        <div style={{ padding: '0.9rem 1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#991b1b', marginBottom: '1rem', fontSize: '0.875rem' }}>
          {erro}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ ...cardStyle, padding: '2rem', textAlign: 'center', color: 'var(--gray-400)' }}>
          Consultando o MV...
        </div>
      )}

      {/* Resultados */}
      {resultado && !loading && (
        <div style={cardStyle}>
          <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--gray-100)', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.82rem', color: 'var(--gray-500)' }}>
              {resultado.total.toLocaleString('pt-BR')} registro{resultado.total !== 1 ? 's' : ''}
            </span>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
              <button onClick={exportarCSV} style={{ padding: '0.3rem 0.75rem', border: '1px solid var(--gray-200)', borderRadius: '5px', background: 'var(--gray-100)', color: 'var(--gray-600)', cursor: 'pointer', fontSize: '0.8rem' }}>
                ↓ CSV
              </button>
              {isAdmin && sqlGerado && (
                <button onClick={() => setShowSql((v) => !v)} style={{ padding: '0.3rem 0.75rem', border: '1px solid var(--gray-200)', borderRadius: '5px', background: 'var(--gray-100)', color: 'var(--gray-600)', cursor: 'pointer', fontSize: '0.8rem' }}>
                  {showSql ? 'Ocultar SQL' : 'Ver SQL'}
                </button>
              )}
            </div>
          </div>

          {showSql && sqlGerado && (
            <div style={{ padding: '0.75rem 1rem', background: '#1e293b', borderBottom: '1px solid var(--gray-100)' }}>
              <pre style={{ margin: 0, fontSize: '0.78rem', color: '#94a3b8', overflowX: 'auto', whiteSpace: 'pre-wrap' }}>{sqlGerado}</pre>
            </div>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)' }}>
                  {resultado.colunas.map((col) => (
                    <th key={col.id} style={{ padding: '0.7rem 1rem', textAlign: col.tipo === 'numero' ? 'right' : 'left', fontWeight: 600, color: 'var(--gray-600)', borderBottom: '2px solid var(--gray-200)', whiteSpace: 'nowrap', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {resultado.linhas.length === 0 ? (
                  <tr><td colSpan={resultado.colunas.length} style={{ padding: '2rem', textAlign: 'center', color: 'var(--gray-400)' }}>Nenhum resultado</td></tr>
                ) : (
                  resultado.linhas.map((row, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? 'white' : 'var(--gray-50)' }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = '#eff6ff')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = i % 2 === 0 ? 'white' : 'var(--gray-50)')}
                    >
                      {resultado.colunas.map((col) => (
                        <td key={col.id} style={{ padding: '0.55rem 1rem', color: 'var(--gray-700)', textAlign: col.tipo === 'numero' ? 'right' : 'left', fontVariantNumeric: col.tipo === 'numero' ? 'tabular-nums' : 'normal' }}>
                          {row[col.id] === null || row[col.id] === undefined
                            ? <span style={{ color: 'var(--gray-300)' }}>—</span>
                            : col.tipo === 'numero'
                              ? Number(row[col.id]).toLocaleString('pt-BR', { maximumFractionDigits: 2 })
                              : String(row[col.id])}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Componente principal
// ============================================================
export default function Relatorios() {
  const { user } = useAuth();

  const [modo, setModo] = useState('ia'); // 'ia' | 'construtor'
  const [dicionario, setDicionario] = useState(null);
  const [setores, setSetores] = useState([]);

  const [selectedDims, setSelectedDims] = useState([]);
  const [selectedMets, setSelectedMets] = useState(['total_atendimentos']);
  const [activeTab, setActiveTab] = useState('dimensoes');

  const [filtros, setFiltros] = useState({
    data_inicio: '',
    data_fim: '',
    setor_id: '',
    status: '',
  });
  const [limite, setLimite] = useState(500);

  const [resultado, setResultado] = useState(null);
  const [sqlGerado, setSqlGerado] = useState(null);
  const [showSql, setShowSql] = useState(false);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);

  const intervalRef = useRef(null);
  const executarRef = useRef(null);

  // Carregar dicionário e setores
  useEffect(() => {
    api.get('/relatorios/dicionario').then((r) => setDicionario(r.data)).catch(() => {});
    api.get('/setores').then((r) => setSetores(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  // Função de execução
  const executar = useCallback(async () => {
    if (selectedMets.length === 0) return;
    setLoading(true);
    setErro(null);
    try {
      const r = await api.post('/relatorios/executar', {
        dimensoes: selectedDims,
        metricas: selectedMets,
        filtros: {
          data_inicio: filtros.data_inicio || null,
          data_fim: filtros.data_fim || null,
          setor_id: filtros.setor_id ? parseInt(filtros.setor_id) : null,
          status: filtros.status || null,
        },
        limite,
      });
      setResultado(r.data);
      setSqlGerado(r.data.sql_gerado || null);
      setUltimaAtualizacao(new Date().toLocaleTimeString('pt-BR'));
    } catch (e) {
      setErro(e.response?.data?.detail || 'Erro ao executar consulta');
    } finally {
      setLoading(false);
    }
  }, [selectedDims, selectedMets, filtros, limite]);

  // Manter ref atualizada para o setInterval não capturar closure stale
  useEffect(() => {
    executarRef.current = executar;
  }, [executar]);

  // Auto-refresh
  useEffect(() => {
    clearInterval(intervalRef.current);
    if (!autoRefresh) return;
    intervalRef.current = setInterval(() => executarRef.current(), 30000);
    return () => clearInterval(intervalRef.current);
  }, [autoRefresh]);

  const toggleDim = (id) =>
    setSelectedDims((prev) => (prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]));

  const toggleMet = (id) =>
    setSelectedMets((prev) => (prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, m]));

  // Export CSV client-side
  const exportarCSV = () => {
    if (!resultado) return;
    const { colunas, linhas } = resultado;
    const escape = (v) => {
      const s = v === null || v === undefined ? '' : String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = colunas.map((c) => c.label).join(',');
    const rows = linhas.map((row) => colunas.map((c) => escape(row[c.id])).join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `relatorio_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const isAdmin = user?.perfil === 'administrador';
  const canShowChart = selectedDims.length === 1 && selectedMets.length >= 1;

  return (
    <div style={{ padding: '1.5rem', maxWidth: '1400px' }}>
      {/* Cabeçalho */}
      <div style={{ marginBottom: '1.25rem', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ fontSize: '1.35rem', fontWeight: 700, color: 'var(--gray-800)', margin: 0 }}>
            Inteligência
          </h1>
          <p style={{ color: 'var(--gray-500)', fontSize: '0.875rem', marginTop: '0.25rem' }}>
            {modo === 'ia'
              ? 'Pergunte em português: a IA gera o SQL e traz o resultado'
              : modo === 'mv'
                ? 'Relatórios do plano de saúde (MV), dados do sistema legado'
                : 'Selecione dimensões e métricas para montar a consulta'}
          </p>
        </div>
        {/* Seletor de modo */}
        <div style={{ display: 'flex', background: 'var(--gray-100)', borderRadius: '8px', padding: '3px', border: '1px solid var(--gray-200)' }}>
          {[
            { key: 'ia', label: '✦ Perguntar à IA' },
            { key: 'construtor', label: '⊞ Construtor' },
            { key: 'mv', label: '⛃ MV / Plano' },
          ].map((m) => (
            <button
              key={m.key}
              onClick={() => setModo(m.key)}
              style={{
                padding: '0.4rem 1rem',
                border: 'none',
                borderRadius: '6px',
                background: modo === m.key ? 'white' : 'transparent',
                color: modo === m.key ? '#2563eb' : 'var(--gray-500)',
                fontWeight: modo === m.key ? 700 : 500,
                fontSize: '0.84rem',
                cursor: 'pointer',
                boxShadow: modo === m.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.15s',
              }}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* ===== MODO IA ===== */}
      {modo === 'ia' && <ModoIA isAdmin={isAdmin} />}

      {/* ===== MODO MV / PLANO ===== */}
      {modo === 'mv' && <ModoMV isAdmin={isAdmin} />}

      {/* ===== MODO CONSTRUTOR ===== */}
      {modo === 'construtor' && (<>

      {/* Filtros */}
      <div style={{ ...cardStyle, padding: '1rem', marginBottom: '1rem', display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label style={labelStyle}>Data início</label>
          <input
            type="date"
            value={filtros.data_inicio}
            onChange={(e) => setFiltros((f) => ({ ...f, data_inicio: e.target.value }))}
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Data fim</label>
          <input
            type="date"
            value={filtros.data_fim}
            onChange={(e) => setFiltros((f) => ({ ...f, data_fim: e.target.value }))}
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Setor</label>
          <select
            value={filtros.setor_id}
            onChange={(e) => setFiltros((f) => ({ ...f, setor_id: e.target.value }))}
            style={inputStyle}
          >
            <option value="">Todos</option>
            {setores.map((s) => (
              <option key={s.id} value={s.id}>{s.nome}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Status</label>
          <select
            value={filtros.status}
            onChange={(e) => setFiltros((f) => ({ ...f, status: e.target.value }))}
            style={inputStyle}
          >
            <option value="">Todos</option>
            {(dicionario?.status_validos || ['ABERTO', 'FECHADO', 'PENDENTE', 'CANCELADO']).map((s) => (
              <option key={s} value={s}>{s.replace('_', ' ')}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Limite</label>
          <select
            value={limite}
            onChange={(e) => setLimite(Number(e.target.value))}
            style={{ ...inputStyle, minWidth: '90px' }}
          >
            <option value={100}>100</option>
            <option value={500}>500</option>
            <option value={1000}>1.000</option>
            <option value={5000}>5.000</option>
          </select>
        </div>
      </div>

      {/* Layout principal */}
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>

        {/* Seletor de campos */}
        <div style={{ ...cardStyle, width: '270px', flexShrink: 0, overflow: 'hidden' }}>
          {/* Abas */}
          <div style={{ display: 'flex', borderBottom: '1px solid var(--gray-200)' }}>
            {[
              { key: 'dimensoes', label: `Dimensões (${selectedDims.length})` },
              { key: 'metricas', label: `Métricas (${selectedMets.length})` },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  flex: 1,
                  padding: '0.65rem 0.5rem',
                  border: 'none',
                  background: activeTab === tab.key ? '#2563eb' : 'white',
                  color: activeTab === tab.key ? 'white' : 'var(--gray-600)',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  transition: 'background 0.15s',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Lista de campos */}
          <div style={{ maxHeight: '420px', overflowY: 'auto', padding: '0.5rem' }}>
            {!dicionario && (
              <div style={{ padding: '1rem', color: 'var(--gray-400)', fontSize: '0.85rem', textAlign: 'center' }}>
                Carregando...
              </div>
            )}
            {activeTab === 'dimensoes' &&
              dicionario?.dimensoes.map((d) => (
                <label
                  key={d.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.45rem 0.5rem',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    background: selectedDims.includes(d.id) ? '#eff6ff' : 'transparent',
                    marginBottom: '1px',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedDims.includes(d.id)}
                    onChange={() => toggleDim(d.id)}
                    style={{ accentColor: '#2563eb', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '0.84rem', color: 'var(--gray-700)' }}>{d.label}</span>
                </label>
              ))}
            {activeTab === 'metricas' &&
              dicionario?.metricas.map((m) => (
                <label
                  key={m.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.45rem 0.5rem',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    background: selectedMets.includes(m.id) ? '#eff6ff' : 'transparent',
                    marginBottom: '1px',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedMets.includes(m.id)}
                    onChange={() => toggleMet(m.id)}
                    style={{ accentColor: '#2563eb', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '0.84rem', color: 'var(--gray-700)' }}>{m.label}</span>
                </label>
              ))}
          </div>

          {/* Dica */}
          <div style={{ padding: '0.6rem 0.75rem', borderTop: '1px solid var(--gray-100)', fontSize: '0.75rem', color: 'var(--gray-400)' }}>
            {activeTab === 'dimensoes'
              ? 'Dimensões agrupam os dados (linhas da tabela)'
              : 'Métricas calculam valores por grupo'}
          </div>
        </div>

        {/* Área de resultados */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Barra de ações */}
          <div style={{ display: 'flex', gap: '0.65rem', marginBottom: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={executar}
              disabled={loading || selectedMets.length === 0}
              style={{
                padding: '0.55rem 1.2rem',
                background: selectedMets.length === 0 ? 'var(--gray-300)' : '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: loading || selectedMets.length === 0 ? 'not-allowed' : 'pointer',
                fontWeight: 700,
                fontSize: '0.875rem',
                opacity: loading ? 0.75 : 1,
              }}
            >
              {loading ? 'Executando...' : 'Executar'}
            </button>

            <button
              onClick={() => setAutoRefresh((a) => !a)}
              style={{
                padding: '0.55rem 0.9rem',
                background: autoRefresh ? '#10b981' : 'var(--gray-100)',
                color: autoRefresh ? 'white' : 'var(--gray-600)',
                border: `1px solid ${autoRefresh ? '#10b981' : 'var(--gray-200)'}`,
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.84rem',
                fontWeight: 500,
              }}
            >
              {autoRefresh ? '⏸ Auto 30s' : '▶ Auto-refresh'}
            </button>

            {resultado && (
              <button
                onClick={exportarCSV}
                style={{
                  padding: '0.55rem 0.9rem',
                  background: 'var(--gray-100)',
                  color: 'var(--gray-700)',
                  border: '1px solid var(--gray-200)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.84rem',
                }}
              >
                ↓ Exportar CSV
              </button>
            )}

            {isAdmin && sqlGerado && (
              <button
                onClick={() => setShowSql((v) => !v)}
                style={{
                  padding: '0.55rem 0.9rem',
                  background: 'var(--gray-100)',
                  color: 'var(--gray-600)',
                  border: '1px solid var(--gray-200)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.84rem',
                }}
              >
                {showSql ? 'Ocultar SQL' : 'Ver SQL'}
              </button>
            )}

            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {resultado && (
                <span style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>
                  {resultado.total.toLocaleString('pt-BR')} registro{resultado.total !== 1 ? 's' : ''}
                </span>
              )}
              {ultimaAtualizacao && (
                <span style={{ fontSize: '0.75rem', color: autoRefresh ? '#10b981' : 'var(--gray-400)' }}>
                  {autoRefresh ? '● ' : ''}Atualizado às {ultimaAtualizacao}
                </span>
              )}
            </div>
          </div>

          {/* SQL gerado (só admin) */}
          {showSql && sqlGerado && (
            <div style={{ ...cardStyle, marginBottom: '1rem', padding: '0.75rem', background: '#1e293b' }}>
              <pre style={{ margin: 0, fontSize: '0.78rem', color: '#94a3b8', overflowX: 'auto', whiteSpace: 'pre-wrap' }}>
                {sqlGerado}
              </pre>
            </div>
          )}

          {/* Erro */}
          {erro && (
            <div style={{ padding: '0.9rem 1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#991b1b', marginBottom: '1rem', fontSize: '0.875rem' }}>
              {erro}
            </div>
          )}

          {/* Estado vazio */}
          {!resultado && !loading && !erro && (
            <div style={{ ...cardStyle, padding: '3rem', textAlign: 'center', color: 'var(--gray-400)' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>📊</div>
              <div style={{ fontWeight: 600, color: 'var(--gray-500)', marginBottom: '0.25rem' }}>
                Nenhum relatório executado
              </div>
              <div style={{ fontSize: '0.85rem' }}>
                Selecione ao menos uma métrica e clique em Executar
              </div>
            </div>
          )}

          {/* Resultados */}
          {resultado && (
            <div style={cardStyle}>
              {/* Gráfico de barras (somente 1 dim + 1+ métricas + ≤30 linhas) */}
              {canShowChart && resultado.linhas.length > 0 && resultado.linhas.length <= 30 && (
                <BarChart
                  linhas={resultado.linhas}
                  dimensaoId={selectedDims[0]}
                  metricaId={selectedMets[0]}
                  dimLabel={dicionario?.dimensoes.find((d) => d.id === selectedDims[0])?.label || selectedDims[0]}
                  metLabel={dicionario?.metricas.find((m) => m.id === selectedMets[0])?.label || selectedMets[0]}
                />
              )}

              {/* Tabela */}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr style={{ background: 'var(--gray-50)' }}>
                      {resultado.colunas.map((col) => (
                        <th
                          key={col.id}
                          style={{
                            padding: '0.7rem 1rem',
                            textAlign: col.tipo === 'numero' ? 'right' : 'left',
                            fontWeight: 600,
                            color: 'var(--gray-600)',
                            borderBottom: '2px solid var(--gray-200)',
                            whiteSpace: 'nowrap',
                            fontSize: '0.8rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.03em',
                          }}
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {resultado.linhas.length === 0 ? (
                      <tr>
                        <td
                          colSpan={resultado.colunas.length}
                          style={{ padding: '2rem', textAlign: 'center', color: 'var(--gray-400)' }}
                        >
                          Nenhum resultado encontrado para os filtros selecionados
                        </td>
                      </tr>
                    ) : (
                      resultado.linhas.map((row, i) => (
                        <tr
                          key={i}
                          style={{
                            borderBottom: '1px solid var(--gray-100)',
                            background: i % 2 === 0 ? 'white' : 'var(--gray-50)',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = '#eff6ff')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = i % 2 === 0 ? 'white' : 'var(--gray-50)')}
                        >
                          {resultado.colunas.map((col) => (
                            <td
                              key={col.id}
                              style={{
                                padding: '0.55rem 1rem',
                                color: 'var(--gray-700)',
                                textAlign: col.tipo === 'numero' ? 'right' : 'left',
                                fontVariantNumeric: col.tipo === 'numero' ? 'tabular-nums' : 'normal',
                              }}
                            >
                              {row[col.id] === null || row[col.id] === undefined
                                ? <span style={{ color: 'var(--gray-300)' }}>—</span>
                                : col.tipo === 'numero'
                                  ? Number(row[col.id]).toLocaleString('pt-BR', { maximumFractionDigits: 2 })
                                  : String(row[col.id])}
                            </td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
      </>)}
    </div>
  );
}
